export const SCHEDULE_DAYS = [
  { value: 0, label: "Sun", full: "Sunday" },
  { value: 1, label: "Mon", full: "Monday" },
  { value: 2, label: "Tue", full: "Tuesday" },
  { value: 3, label: "Wed", full: "Wednesday" },
  { value: 4, label: "Thu", full: "Thursday" },
  { value: 5, label: "Fri", full: "Friday" },
  { value: 6, label: "Sat", full: "Saturday" },
];

export const SCHEDULE_RECURRENCES = [
  { value: "weekly", label: "Weekly", hint: "Same day every week" },
  { value: "daily", label: "Daily", hint: "Every day at this time" },
  { value: "monthly", label: "Monthly", hint: "Same date every month" },
  { value: "once", label: "Once", hint: "Run one time only" },
];

export function normalizeScheduleRecurrence(value) {
  if (SCHEDULE_RECURRENCES.some((r) => r.value === value)) return value;
  return "weekly";
}

export function getDayLabel(dayOfWeek, { full = false } = {}) {
  const day = SCHEDULE_DAYS.find((d) => d.value === dayOfWeek);
  if (!day) return full ? "Monday" : "Mon";
  return full ? day.full : day.label;
}

export function formatTime12h(timeStr) {
  const normalized = normalizeScheduleTimeInput(timeStr);
  const [h, m] = normalized.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function formatScheduleSummary({
  recurrence = "weekly",
  dayOfWeek,
  timeStr,
  timeZone,
  nextScheduledAt,
} = {}) {
  const t = formatTime12h(timeStr || "09:00");
  const tz = formatTimezoneShort(timeZone);

  switch (normalizeScheduleRecurrence(recurrence)) {
    case "once": {
      if (nextScheduledAt) {
        const d = new Date(nextScheduledAt);
        if (!Number.isNaN(d.getTime())) {
          const dateLabel = d.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          return `Once · ${dateLabel} at ${t} · ${tz}`;
        }
      }
      return `Once at ${t} · ${tz}`;
    }
    case "daily":
      return `Daily at ${t} · ${tz}`;
    case "monthly": {
      const dayNum = nextScheduledAt
        ? new Date(nextScheduledAt).getDate()
        : null;
      return dayNum
        ? `Monthly on day ${dayNum} at ${t} · ${tz}`
        : `Monthly at ${t} · ${tz}`;
    }
    default: {
      const day = getDayLabel(dayOfWeek, { full: true });
      return `Weekly · ${day} at ${t} · ${tz}`;
    }
  }
}

export function formatScheduleCardLabel({
  recurrence = "weekly",
  dayOfWeek,
  timeStr,
  nextScheduledAt,
} = {}) {
  const t = formatTime12h(timeStr || "09:00");

  switch (normalizeScheduleRecurrence(recurrence)) {
    case "once": {
      if (nextScheduledAt) {
        const d = new Date(nextScheduledAt);
        if (!Number.isNaN(d.getTime())) {
          const dateLabel = d.toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          return `Once on ${dateLabel} at ${t}`;
        }
      }
      return `Once at ${t}`;
    }
    case "daily":
      return `Every day at ${t}`;
    case "monthly": {
      const dayNum = nextScheduledAt
        ? new Date(nextScheduledAt).getDate()
        : null;
      return dayNum ? `Monthly on day ${dayNum} at ${t}` : `Monthly at ${t}`;
    }
    default: {
      const day = getDayLabel(dayOfWeek, { full: true });
      return `Every ${day} at ${t}`;
    }
  }
}

export function isScheduleRunDue(nextScheduledAt, now = new Date()) {
  if (!nextScheduledAt) return false;
  const nextAt = new Date(nextScheduledAt);
  if (Number.isNaN(nextAt.getTime())) return false;
  return nextAt <= now;
}

export function isScheduleRunActive(
  { schedule_enabled: scheduleEnabled, schedule_locked_until: lockedUntil } = {},
  now = new Date(),
) {
  if (!scheduleEnabled || !lockedUntil) return false;

  const lockExpiry = new Date(lockedUntil);
  return !Number.isNaN(lockExpiry.getTime()) && lockExpiry > now;
}

export function isScheduleRunDueButWaiting(
  mapping,
  now = new Date(),
) {
  if (!mapping?.schedule_enabled || !mapping?.next_scheduled_at) return false;
  if (isScheduleRunActive(mapping, now)) return false;
  return isScheduleRunDue(mapping.next_scheduled_at, now);
}

export function formatNextRunDisplay(
  nextScheduledAt,
  {
    scheduleEnabled = true,
    scheduleLockedUntil = null,
    now = new Date(),
  } = {},
) {
  if (!scheduleEnabled) return "–";
  if (!nextScheduledAt) return "–";

  if (scheduleLockedUntil) {
    const lockExpiry = new Date(scheduleLockedUntil);
    if (!Number.isNaN(lockExpiry.getTime()) && lockExpiry > now) {
      return "Running now";
    }
  }

  if (isScheduleRunDue(nextScheduledAt, now)) {
    return "Due now";
  }

  return new Date(nextScheduledAt).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatRecurrenceHint(recurrence, timeZone) {
  const item = SCHEDULE_RECURRENCES.find((r) => r.value === recurrence);
  const tz = formatTimezoneShort(timeZone);
  return `${item?.hint || "Scheduled run"} · ${tz}`;
}

export function computeNextWeekdayDate(
  dayOfWeek,
  timeStr,
  fromDate = new Date(),
) {
  const [hours, minutes] = normalizeScheduleTimeInput(timeStr)
    .split(":")
    .map(Number);
  const now = new Date(fromDate);
  let daysUntil = (dayOfWeek - now.getDay() + 7) % 7;
  const candidate = new Date(now);
  candidate.setHours(hours, minutes, 0, 0);
  if (daysUntil === 0 && candidate <= now) daysUntil = 7;
  candidate.setDate(now.getDate() + daysUntil);
  candidate.setHours(hours, minutes, 0, 0);
  return candidate;
}

export function computeNextMonthDayDate(
  dayOfMonth,
  timeStr,
  fromDate = new Date(),
) {
  const [hours, minutes] = normalizeScheduleTimeInput(timeStr)
    .split(":")
    .map(Number);
  const now = new Date(fromDate);
  const clampDay = (year, month, day) =>
    Math.min(day, new Date(year, month + 1, 0).getDate());

  let year = now.getFullYear();
  let month = now.getMonth();
  let day = clampDay(year, month, dayOfMonth);
  let candidate = new Date(year, month, day, hours, minutes, 0, 0);

  if (candidate <= now) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
    day = clampDay(year, month, dayOfMonth);
    candidate = new Date(year, month, day, hours, minutes, 0, 0);
  }

  return candidate;
}

export function computeNextDailyDate(timeStr, fromDate = new Date()) {
  const [hours, minutes] = normalizeScheduleTimeInput(timeStr)
    .split(":")
    .map(Number);
  const candidate = new Date(fromDate);
  candidate.setHours(hours, minutes, 0, 0);
  if (candidate <= fromDate) {
    candidate.setDate(candidate.getDate() + 1);
  }
  return candidate;
}

export function computeNextRunDate({
  recurrence = "weekly",
  time = "09:00",
  dayOfWeek = getDefaultScheduleDay(),
  dateValue,
} = {}) {
  const normalized = normalizeScheduleRecurrence(recurrence);

  if (normalized === "once") {
    if (!dateValue) return null;
    const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const [, y, m, d] = match.map(Number);
    const [hours, minutes] = normalizeScheduleTimeInput(time)
      .split(":")
      .map(Number);
    return new Date(y, m - 1, d, hours, minutes, 0, 0);
  }

  if (normalized === "daily") {
    return computeNextDailyDate(time);
  }

  if (normalized === "monthly") {
    const dayOfMonth = dateValue
      ? parseInt(dateValue.split("-")[2], 10)
      : new Date().getDate();
    return computeNextMonthDayDate(dayOfMonth, time);
  }

  return computeNextWeekdayDate(dayOfWeek, time);
}

export function syncScheduleDateForRecurrence(
  recurrence,
  timeValue,
  dateValue,
) {
  const normalized = normalizeScheduleRecurrence(recurrence);

  if (normalized === "once") {
    const base = dateValue?.match(/^\d{4}-\d{2}-\d{2}$/)
      ? dateValue
      : toDateValue(new Date());
    return base;
  }

  if (normalized === "daily") {
    return toDateValue(computeNextDailyDate(timeValue));
  }

  if (normalized === "monthly") {
    const dayOfMonth = dateValue
      ? parseInt(dateValue.split("-")[2], 10)
      : new Date().getDate();
    return toDateValue(computeNextMonthDayDate(dayOfMonth, timeValue));
  }

  const dayOfWeek = dateValue
    ? new Date(
        parseInt(dateValue.slice(0, 4), 10),
        parseInt(dateValue.slice(5, 7), 10) - 1,
        parseInt(dateValue.slice(8, 10), 10),
      ).getDay()
    : getDefaultScheduleDay();

  return toDateValue(computeNextWeekdayDate(dayOfWeek, timeValue));
}

function toDateValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatDraftSchedulePreview({
  recurrence = "weekly",
  timeValue = "09:00",
  dateValue,
  dayOfWeek = getDefaultScheduleDay(),
  timezone = getBrowserTimezone(),
} = {}) {
  const time = formatTime12h(timeValue);
  const tz = formatTimezoneShort(timezone);
  const normalized = normalizeScheduleRecurrence(recurrence);
  const nextRun = computeNextRunDate({
    recurrence: normalized,
    time: timeValue,
    dayOfWeek,
    dateValue,
  });

  const nextRunLabel =
    nextRun && !Number.isNaN(nextRun.getTime())
      ? nextRun.toLocaleString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;

  switch (normalized) {
    case "once":
      return dateValue
        ? `Runs once on ${formatDateLabel(dateValue)} at ${time}. Next run: ${nextRunLabel || "—"}.`
        : `Runs once — pick a date, then choose a time (${tz}).`;
    case "daily":
      return `Runs every day at ${time} (${tz}).${nextRunLabel ? ` Next run: ${nextRunLabel}.` : ""}`;
    case "monthly": {
      const dayNum = dateValue
        ? parseInt(dateValue.split("-")[2], 10)
        : new Date().getDate();
      return `Runs on day ${dayNum} of each month at ${time} (${tz}).${nextRunLabel ? ` Next run: ${nextRunLabel}.` : ""}`;
    }
    default:
      return `Runs every ${getDayLabel(dayOfWeek, { full: true })} at ${time} (${tz}).${nextRunLabel ? ` Next run: ${nextRunLabel}.` : ""}`;
  }
}

function formatDateLabel(dateValue) {
  const match = dateValue?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dateValue;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatScheduleModalSummary({
  recurrence = "weekly",
  timeValue = "09:00",
  dateValue,
  dayOfWeek = getDefaultScheduleDay(),
  timezone = getBrowserTimezone(),
} = {}) {
  const schedule = formatScheduleCardLabel({
    recurrence,
    dayOfWeek,
    timeStr: timeValue,
    nextScheduledAt: dateValue ? `${dateValue}T${timeValue}:00` : undefined,
  });
  const tz = formatTimezoneShort(timezone);
  const nextRun = computeNextRunDate({
    recurrence: normalizeScheduleRecurrence(recurrence),
    time: timeValue,
    dayOfWeek,
    dateValue,
  });
  const nextLabel =
    nextRun && !Number.isNaN(nextRun.getTime())
      ? nextRun.toLocaleString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;

  if (nextLabel) {
    return `${schedule} · ${tz} · Next run ${nextLabel}`;
  }
  return `${schedule} · ${tz}`;
}

export function scheduleFieldsFromDate(date, timezone, recurrence = "weekly") {
  if (!date || Number.isNaN(date.getTime())) {
    return {
      scheduleDayOfWeek: getDefaultScheduleDay(),
      scheduleTime: "09:00",
      scheduleTimezone: timezone || getBrowserTimezone(),
      scheduleRecurrence: normalizeScheduleRecurrence(recurrence),
    };
  }
  return {
    scheduleDayOfWeek: date.getDay(),
    scheduleTime: normalizeScheduleTimeInput(
      `${date.getHours()}:${date.getMinutes()}`,
    ),
    scheduleTimezone: timezone || getBrowserTimezone(),
    scheduleRecurrence: normalizeScheduleRecurrence(recurrence),
  };
}

export function dateFromScheduleFields({
  dayOfWeek = getDefaultScheduleDay(),
  time = "09:00",
  nextScheduledAt,
} = {}) {
  if (nextScheduledAt) {
    const parsed = new Date(nextScheduledAt);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  const [hours, minutes] = normalizeScheduleTimeInput(time)
    .split(":")
    .map(Number);
  const now = new Date();
  const candidate = new Date(now);
  candidate.setHours(hours, minutes, 0, 0);

  let daysUntil = (dayOfWeek - now.getDay() + 7) % 7;
  if (daysUntil === 0 && candidate <= now) daysUntil = 7;
  candidate.setDate(now.getDate() + daysUntil);
  candidate.setHours(hours, minutes, 0, 0);
  return candidate;
}

export function formatSchedulePickerLabel(date, timeZone) {
  if (!date || Number.isNaN(date.getTime())) return "Pick date & time";
  const day = date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
  return `${day} at ${formatTime12h(
    `${date.getHours()}:${date.getMinutes()}`,
  )} · ${formatTimezoneShort(timeZone)}`;
}

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function getDefaultScheduleDay() {
  return new Date().getDay();
}

export function normalizeScheduleTimeInput(value) {
  if (!value || typeof value !== "string") return "09:00";
  const match = value.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return "09:00";
  const hours = Math.min(23, Math.max(0, parseInt(match[1], 10)));
  const minutes = Math.min(59, Math.max(0, parseInt(match[2], 10)));
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function formatTimezoneShort(timeZone) {
  if (!timeZone || timeZone === "UTC") return "UTC";
  const parts = timeZone.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
}
